import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useMemo, useState } from "react";

import useFetchData from "../hooks/useFetchData";
import { categorizeData, fetchMetricsData, fetchUnemploymentRateChartData, getSvgMap } from "../utils/dashboardUtils";

import LegendMetricModal from "../components/LegendMetricModal";

import ChildFamilyMetrics from "../components/panels/ChildFamilyMetrics";
import EducationMetrics from "../components/panels/EducationMetrics";
import EconomyMetrics from "../components/panels/EconomyMetrics";
import HousingMetrics from "../components/panels/HousingMetrics";
import AccessibilityMetrics from "../components/panels/AccessibilityMetrics";
import DemographicMetrics from "../components/panels/DemographicMetrics";
import { handleExcelDownload } from "../utils/excelExportModule";
import DownloadLoading from "../components/DownloadLoading";
import ClearMapSelectionModal from "../components/ClearMapSelectionModal";
import TabMenu from "../components/TabMenu";
import TabMenuSkeleton from "../components/skeletons/TabMenuSkeleton";
import MetricCategoriesContext from "../context/MetricCategoriesContext";
import MetricCardSkeleton from "../components/skeletons/MetricCardSkeleton";
import printSvg from '../assets/print.svg';
import MetricsPDF from "./MetricsPDF";
import { pdf } from '@react-pdf/renderer';
import PDFLoading from "../components/PDFLoading";
import { base64ToBlob, tabColors } from "../utils/helpers";
import LiveRegion from "../components/accessibility/LiveRegion";
import { announce } from "../utils/a11y";
import PopupBlockedBanner from "../components/accessibility/PopupBlockedBanner";
import { DashboardColorContext } from "../context/DashboardColorContext";

const tabs = ["Child & Family", "Education", "Economy", "Housing", "Accessibility", "Demographic"];

const Dashboard = () => {
    const isIframe = window.self !== window.top;
    const navigate = useNavigate();
    const { tab } = useParams<{ tab: string }>();
    const { metricCategories, loading: categoriesLoading } = useContext(MetricCategoriesContext);

    // Message prop state
    const [currentTab, setCurrentTab] = useState<string>("");
    const [selectedArea, setSelectedArea] = useState('');
    const [legendId, setLegendId] = useState(0);

    // Modal state
    const [isLegendModalOpen, setIsLegendModalOpen] = useState(false);
    const [shouldDownloadExcel, setShouldDownloadExcel] = useState(false);
    const [showDownloadLoading, setShowDownloadLoading] = useState(false);
    const [showPDFLoading, setShowPDFLoading] = useState(false);
    const [popupBlocked, setPopupBlocked] = useState(false);

    // Audio cue state
    const [liveMessage, setLiveMessage] = useState('');

    const { indicators, loading, dashboardView, geoDetails, geoTag, lodMissing } = useFetchData(currentTab || '');

    // single derived flag for pdf or download loading state
    const isWorking = showDownloadLoading || showPDFLoading;

    // "Ready" flag and tracking metric tabs
    const isLoading = categoriesLoading ||
    loading;

    const isMetricTab = useMemo(() => {
        if (categoriesLoading || !metricCategories.length || !currentTab) return undefined;
        
        return (
            !!currentTab &&
            metricCategories.length > 0 &&
            metricCategories.map(c => c.toLowerCase()).includes(currentTab.toLowerCase())
        );
    }, [metricCategories, currentTab]);

    // #region React useEffects
    useEffect(() => {
        if (tabs.length === 0) return;

        if (!isIframe) {
            if (tab && tabs.includes(tab)) {
                setCurrentTab(tab.toLowerCase());
            } else if (!currentTab) {
                setCurrentTab(tabs[0]);
            }
        }
    }, [tab, isIframe, tabs]);

    useEffect(() => {
        if (tabs.length === 0) return;

        if (isIframe && dashboardView) {
            setCurrentTab(tabs.find((t) => t.toLowerCase() === dashboardView.toLowerCase()) || tabs[0]);
        } else {
            // When not in iframe, just ensure the currentTab is set based on the `tab` param
            setCurrentTab(tabs.find((t) => t.toLowerCase() === currentTab?.toLowerCase()) || tabs[0]);
        }
    }, [dashboardView, isIframe, tabs]);

    // Combine data from all sources
    const allDataAvailable = useMemo(() => {
        return !loading && indicators;
    }, [loading, indicators]);

    useEffect(() => {
        const handleMessage = async (event: MessageEvent) => {
            if (event.data.message === 'pdf') {
                announce("Generating PDF. Please wait.");
                setShowPDFLoading(true);

                const mapUrl = event.data.params.url;
                const logo = event.data.params.logoUrl || '';
                const legend = event.data.params.legendUrl || '';
                const selection = event.data.params.selectedArea || '';
                // const site = event.data.params.siteUrl || '';
                setSelectedArea(selection);

                const imageBlob = base64ToBlob(mapUrl.split(',')[1], 'image/png');

                // Open a blank window synchronously (not blocked)
                const newWindow = window.open('https://caseydashboard-staging.caimaps.net/waiting', '_blank');

                // If blocked, inform the user and stop
                if (!newWindow) {
                    console.warn('Popup blocked by browser');
                    setPopupBlocked(true);
                    announce('PDF popup was blocked. Please allow popups for this site and try again.');
                    setShowPDFLoading(false);
                    return;
                }
        
                try {
                    // Fetch the data first
                    const disaggregatedData = await fetchMetricsData(tabs, geoDetails, geoTag);
                    const unemploymentRateChart = await fetchUnemploymentRateChartData(geoDetails, geoTag, disaggregatedData);

                    // Render the MetricsPDF
                    const blob = await pdf(
                    <MetricsPDF
                        pdfImageBlob={imageBlob}
                        logoUrl={logo}
                        legendUrl={legend}
                        selectedArea={selection}
                        disaggregatedData={disaggregatedData}
                        unemploymentRateChart={unemploymentRateChart}
                    />
                    ).toBlob();

                    const url = URL.createObjectURL(blob);
                    // window.open(url, '_blank');

                    if (newWindow) {
                        newWindow.location.href = url;
                    }

                    announce('PDF is ready. The file has opened in a new tab.');
                } catch (err) {
                    console.error('Error generating PDF:', err);
                    announce('PDF generation failed. Please try again.');
                } finally {
                    // small delay to ensure SR can speak
                    setTimeout(() => setShowPDFLoading(false), 800);
                }

                //LEGACY PRINT LOGIC
                /*setIsModalOpen(true);
            
                // Send message to expand
                window.parent.postMessage({ 
                    message: "expand",
                    mode: "fullscreen"
                }, '*');
                console.log('expand message sent');
                */
            } else if (event.data.message === 'metric-modal') {

                // Legend metadata info icon event
                console.log('Message received from parent app inside legend metric modal:', event.data);
                setLegendId(event.data.params.id);
            } else if (event.data.message === 'excel') {
                // Get selected area
                const sArea = event.data?.params?.selectedArea;

                announce('Creating Excel workbook. Please wait.');
                // Show loading modal
                setShowDownloadLoading(true);
                
                if (!loading) {
                    // Execute excel download script
                    handleExcelDownload(geoDetails, geoTag, tabs, sArea)
                        .then(() => announce('Excel workbook is ready. Your download will start automatically.'))
                        .catch(() => announce('Download failed. Please try again.'))
                        .finally(() => setTimeout(() => setShowDownloadLoading(false), 800));
                } else {
                    console.log('Data is still loading, will attempt to download Excel later.');
                    setShouldDownloadExcel(true); // Set flag to retry download
                }
            }
        };
    
        window.addEventListener('message', handleMessage);
    
        return () => {
          window.removeEventListener('message', handleMessage);
        };
    }, [allDataAvailable, geoDetails]);

    useEffect(() => {
        if (!loading && shouldDownloadExcel) {
            announce('Creating Excel workbook. Please wait.');
            setShowDownloadLoading(true);
            handleExcelDownload(geoDetails, geoTag, tabs, selectedArea)
            .then(() => announce('Excel workbook is ready. Your download will start automatically.'))
            .catch(() => announce('Download failed. Please try again.'))
            .finally(() => {
                setShouldDownloadExcel(false);
                setTimeout(() => setShowDownloadLoading(false), 800);
            });
        }
    }, [loading, shouldDownloadExcel, geoDetails, geoTag]);

    // Announce when loading starts/ends or the tab changes
    useEffect(() => {
        if (isMetricTab === undefined) return;

        if (isLoading) {
        setLiveMessage(`Loading ${currentTab} metrics`);
        } else {
        setLiveMessage(`${currentTab} metrics loaded`);
        }
    }, [isLoading, currentTab, isMetricTab]);

    //#endregion

    //#region Event Handlers
    const closeLegendModal = () => {
        setIsLegendModalOpen(false);
        setLegendId(-1);
    };

    const handlePrintNoIframe = async () => {
        announce('Generating PDF. Please wait.');
        setShowPDFLoading(true);

        // Open a blank window synchronously (not blocked)
        const newWindow = window.open('https://caseydashboard-staging.caimaps.net/waiting', '_blank');

        // If blocked, inform the user and stop
        if (!newWindow) {
            console.warn('Popup blocked by browser');
            setPopupBlocked(true);
            announce('PDF popup was blocked. Please allow popups for this site and try again.');
            setShowPDFLoading(false);
            return;
        }
        
        try {
            // Fetch the data first
            const disaggregatedData = await fetchMetricsData(tabs, geoDetails, geoTag);
            const unemploymentRateChart = await fetchUnemploymentRateChartData(geoDetails, geoTag, disaggregatedData);

            // Render MetricsPDF with fully available data
            const blob = await pdf(
            <MetricsPDF
                logoUrl={''}
                legendUrl={''}
                selectedArea={''}
                disaggregatedData={disaggregatedData}
                unemploymentRateChart={unemploymentRateChart}
            />
            ).toBlob();

            const url = URL.createObjectURL(blob);
            // window.open(url, '_blank');

            if (newWindow) {
                newWindow.location.href = url;
            }

            announce('PDF is ready. The file has opened in a new tab.');
        } catch (err) {
            console.error('Error generating PDF:', err);
            announce('PDF generation failed. Please try again.');
        } finally {
            setTimeout(() => setShowPDFLoading(false), 800);
        }
    };
    //#endregion

    const categorizedData = categorizeData(indicators);

    const renderMetrics = (tab: string) => {
        const noMetrics =
        !isLoading &&
        isMetricTab &&
        indicators.length === 0;

        const color = tabColors[tab] || "#337AB7";

        // Map tab names to corresponding components
        const metricsComponents: Record<string, JSX.Element> = {
            "Child & Family": <ChildFamilyMetrics indicators={indicators} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag}/> ,
            Education: <EducationMetrics indicators={indicators} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag}/>,
            Economy: <EconomyMetrics indicators={indicators} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag}/>,
            Accessibility: <AccessibilityMetrics indicators={indicators} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag}/>,
            Demographic: <DemographicMetrics indicators={indicators} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag}/>,
            Housing: <HousingMetrics indicators={indicators} categorizedData={categorizedData} tab={tab} geoDetails={geoDetails} geoTag={geoTag}/>
        };

        return (
            <>
                <div>
                    {noMetrics ? (
                        <div className="flex items-center justify-center text-sm text-color-cai-dashboard">
                            No metrics available for {tab}.
                        </div>
                    ) : (
                        <div className="h-full">
                            <DashboardColorContext.Provider value={color}>
                                {metricsComponents[tab]}
                            </DashboardColorContext.Provider>
                        </div>
                    )}
                </div>
            </>
        );
    };

    // Set active tab to current tab if present
    const activeTab = currentTab?.toLowerCase() || tabs[0];

    // compute a broader busy for loading data OR in-flight actions:
    const isBusyForContent = isLoading || isWorking;

    if (lodMissing) {
        console.log("Rendering ClearMapSelectionModal");

        return (
            <div
                style={{
                    display: "block",
                    width: "100%",
                    minHeight: "320px",
                    backgroundColor: "#ffffff",
                }}
            >
                <ClearMapSelectionModal />
            </div>
        );
    }

    return (
        <div className={`${!isIframe ? 'max-w-[456px] mx-auto print:max-w-full' : 'min-h-80'}`}>
            {/* Functional Icons for Non-iframe View */}
            {!isIframe && (
                <div className="my-2 mx-5 flex items-center justify-end gap-3">
                    <img src={printSvg} alt="print icon" title="Print PDF" className="w-5 h-5 hover:cursor-pointer" onClick={() => { handlePrintNoIframe() }}/>
                    <img src={printSvg} alt="print icon" title="Download Data" className="w-5 h-5 hover:cursor-pointer" onClick={() => handleExcelDownload(geoDetails, geoTag, tabs, selectedArea) }/>
                </div>
            )}

            {/* Tab menu for dashboard categories in non-iframe view */}
            {!isIframe && (
                <div className="sticky top-0 z-10 bg-white">
                    {tabs.length > 0 
                    ? <TabMenu
                        tabs={tabs}
                        activeTab={activeTab}
                        setCurrentTab={setCurrentTab}
                        navigate={navigate}
                    />
                    : <TabMenuSkeleton/>
                    }
                </div>
            )}
            
            <main id="app-content" aria-busy={isBusyForContent ? 'true' : 'false'}>
                {/* Accessible live announcement for loading state */}
                <LiveRegion message={liveMessage} politeness="polite" />

                {/* Render current dashboard view */}
                <div className="no-print min-h-80">
                    {isLoading ? (
                        isMetricTab === undefined ? null :
                        (
                            <>
                                <div className="grid grid-cols-2 w-full mx-10" aria-hidden="true">
                                    {[...Array(6)].map((_, i) => (
                                        <MetricCardSkeleton key={i} />
                                    ))}
                                </div>
                            </>
                        )
                    ) : (
                        renderMetrics(currentTab)
                    )}
                </div>
            </main>


            {/* #region: Modals */}
            <LegendMetricModal
                id={legendId}
                isOpen={isLegendModalOpen}
                onClose={closeLegendModal}
                geoDetails={geoDetails}
                geoTag={geoTag}
                svg={getSvgMap(legendId)}
            />

            {showDownloadLoading &&
                <DownloadLoading/>
            }

            {showPDFLoading && 
                <PDFLoading/>
            }

            {/* PDF Popup blocked */}
            <PopupBlockedBanner
                show={popupBlocked}
                onClose={() => setPopupBlocked(false)}
            />
        </div>
    );
}

export default Dashboard;